/* eslint-disable no-console */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Row, Col, Label, Input, Badge, Button } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { api } from '../../../config';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';
import { formatDateToDDMMYYYY } from '../../../utils/dateformatter';

const getProductIcon = name => {
  const n = (name || '').toLowerCase();
  if (n.includes('propane')) return 'ri-fire-line';
  if (n.includes('butane')) return 'ri-fire-line';
  if (n.includes('lpg')) return 'ri-gas-station-line';
  return 'ri-box-3-line';
};

const InvitationEvaluationTable = ({
  procurementTypeHeader,
  refreshTrigger = 0,
}) => {
  const navigate = useNavigate();
  const token = getAuthToken();
  const searchTimeoutRef = useRef(null);
  const tableContainerRef = useRef(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [sortField, setSortField] = useState('procurementnumber');
  const [sortDirection, setSortDirection] = useState('asc');
  const [data, setData] = useState({ items: [] });
  const [statusCounts, setStatusCounts] = useState({
    total: 0,
    draft: 0,
    submitted: 0,
    byProductType: {},
  });

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchText]);

  const handleSearch = useCallback(e => {
    setSearchText(e.target.value);
  }, []);

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
    if (tableContainerRef.current) {
      requestAnimationFrame(() => {
        tableContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }, []);

  const handleSort = useCallback((column, direction) => {
    const fieldMapping = {
      procurementnumber: 'procurementnumber',
      titleprocurement: 'titleprocurement',
      procurementtype: 'procurementtype',
      description: 'description',
      isProcurementCompleted: 'isProcurementCompleted',
    };

    const sortFieldName =
      fieldMapping[column.id] || column.id || 'procurementnumber';

    requestAnimationFrame(() => {
      setSortField(sortFieldName);
      setSortDirection(direction);
    });
  }, []);

  const getCurrentSortColumnId = useCallback(() => {
    const fieldToColumnMapping = {
      procurementnumber: 'procurementnumber',
      titleprocurement: 'titleprocurement',
      procurementtype: 'procurementtype',
      description: 'description',
      isProcurementCompleted: 'isProcurementCompleted',
    };
    return fieldToColumnMapping[sortField] || 'procurementnumber';
  }, [sortField]);

  const clearFilters = useCallback(() => {
    requestAnimationFrame(() => {
      setSearchText('');
      setCurrentPage(1);
    });
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    requestAnimationFrame(() => {
      setRowsPerPage(newPerPage);
      setCurrentPage(1);
    });
  }, []);

  const procurementTypeMap = {
    '': '-',
    PelelanganKhusus: 'Pelelangan Khusus',
    PemilihanLangsung: 'Pemilihan Langsung',
    TunjukLangsung: 'Tunjuk Langsung',
  };

  useEffect(() => {
    if (!token) {
      toast.error('Authentication token not found. Please login again.');
      return;
    }
    if (!isAuthenticated()) {
      toast.error('Please login to access this page.');
      return;
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    if (!token || !isAuthenticated()) {
      toast.error('Authentication required. Please login again.');
      return;
    }

    setIsLoading(true);
    try {
      let url = `${api.API_URL}/procurement/getall-list-invitation-evaluation?PageNumber=${currentPage}&PageSize=${rowsPerPage}`;

      if (procurementTypeHeader) {
        url += `&ProcurementTypeHeader=${encodeURIComponent(procurementTypeHeader)}`;
      }

      if (debouncedSearchText && debouncedSearchText.trim()) {
        url += `&Search=${encodeURIComponent(debouncedSearchText.trim())}`;
      }

      if (sortField) {
        url += `&SortBy=${sortField}`;
        const isDescending = sortDirection === 'desc' ? 'true' : 'false';
        url += `&IsDescending=${isDescending}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      requestAnimationFrame(() => {
        setData({
          items: result.data.items || [],
        });
        setTotalRows(result.data.totalItems || 0);
      });
    } catch (error) {
      toast.error('Failed to load procurement data');
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearchText,
    sortField,
    sortDirection,
    procurementTypeHeader,
    refreshTrigger,
    token,
  ]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [fetchData, token]);

  const handleDeleteModal = (id = null) => {
    Swal.fire({
      title: 'Are You sure want to Delete this Record?',
      text: 'Deleted Data cannot be Recovered!!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, Delete!',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        handleDelDataProcurement(id);
      }
    });
  };

  const handleDelDataProcurement = async id => {
    try {
      setIsLoading(true);
      await axios.delete(`${api.API_URL}/procurement/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('Data has been deleted');
    } catch (error) {
      setError(error.message);
      toast.error(error);
    } finally {
      setIsLoading(false);
      fetchData();
    }
  };

  const MobileCard = ({ item }) => (
    <div className='card mb-2 d-md-none'>
      <div className='card-body p-3'>
        <div className='d-flex justify-content-between align-items-start mb-2'>
          <h6 className='card-title mb-0 text-truncate flex-grow-1 me-2'>
            {item?.titleProcurement || ''}
          </h6>
          <Badge
            color={item?.isProcurementCompleted ? 'success' : 'info'}
            className='flex-shrink-0'
          >
            {item?.isProcurementCompleted ? 'Submitted' : 'Draft'}
          </Badge>
        </div>

        <div className='mb-2'>
          <small className='text-muted d-block'>
            ID: {item?.procurementNumber || ''}
          </small>
          <small className='text-muted d-block'>
            Type: {procurementTypeMap[item?.procurementType] || '-'}
          </small>
          {item?.productType?.product?.name && (
            <small className='text-muted d-block'>
              Product: {item.productType.product.name}
            </small>
          )}
        </div>

        {item?.description && (
          <p className='card-text text-muted small mb-2 text-truncate'>
            {item.description}
          </p>
        )}

        <div className='d-flex gap-1'>
          <Link
            to={`../apps-procurement/upd/${item?.id || ''}`}
            className='btn btn-sm btn-outline-primary flex-fill'
          >
            <i className='ri-edit-line me-1'></i>Edit
          </Link>
        </div>
      </div>
    </div>
  );

  const columns = React.useMemo(
    () => [
      {
        id: 'procurementnumber',
        name: (
          <span className=' text-uppercase fw-semibold fs-12'>ID Proc </span>
        ),
        selector: row => row?.procurementNumber || '',
        sortable: true,
        width: '10%',
        wrap: true,
        cell: row => {
          return (
            <>
              <h6 className={'mb-0'}>{row?.procurementNumber || ''}</h6>
            </>
          );
        },
      },

      {
        id: 'isInvitePanitiaTenderCompletedDate',
        name: (
          <span className=' text-uppercase fw-semibold fs-12'>
            Submitted Date{' '}
          </span>
        ),
        selector: row => row?.isInvitePanitiaTenderCompletedDate || '',
        sortable: true,
        width: '10%',
        wrap: true,
        cell: row => {
          return (
            <>
              <h6 className={'mb-0'}>
                {formatDateToDDMMYYYY(
                  row?.isInvitePanitiaTenderCompletedDate
                ) || ''}
              </h6>
            </>
          );
        },
      },

      {
        id: 'titleprocurement',
        name: <span className=' text-uppercase fw-semibold fs-12'>Title </span>,
        selector: row => row?.titleProcurement || '',
        sortable: true,
        width: '13%',
        wrap: true,
        cell: row => {
          return (
            <>
              <h6
                className={'mb-0 text-truncate'}
                title={row?.titleProcurement || ''}
              >
                {row?.titleProcurement || ''}
              </h6>
            </>
          );
        },
      },
      {
        id: 'procurementTypeHeaderLabel',
        name: (
          <span className='text-uppercase fw-semibold fs-12'>Proc Type.</span>
        ),
        selector: row => row?.procurementTypeHeaderLabel || '',
        sortable: false,
        width: '12%',
        wrap: true,
        cell: row => {
          return (
            <>
              <div>
                <span className='fw-medium text-truncate d-block'>
                  {procurementTypeMap[row?.procurementType] || '-'}
                </span>{' '}
                {/* <p className='text-muted mb-0 text-truncate small'>
                  {procurementTypeMap[row?.procurementType] || '-'}
                </p> */}
              </div>
            </>
          );
        },
      },

      {
        name: (
          <span className=' text-uppercase fw-semibold fs-12'>
            Product Name
            <br />
            Product Type
          </span>
        ),
        selector: row => row?.productType?.product?.name || '',
        sortable: false,
        left: true,
        wrap: true,
        width: '15%',
        cell: row => {
          return (
            <>
              <div>
                <span className='fw-medium text-truncate d-block'>
                  {row?.productType?.name || null}
                </span>{' '}
                <p className='text-muted mb-0 text-truncate small'>
                  {row?.productType?.description || null}
                </p>
              </div>
            </>
          );
        },
      },
      {
        id: 'description',
        name: (
          <span className=' text-uppercase fw-semibold fs-12'>Description</span>
        ),
        selector: row => row?.description || '',
        sortable: true,
        left: true,
        wrap: true,
        width: '23%',
        cell: row => {
          return (
            <>
              <h6
                className={'mb-0 text-truncate '}
                title={row?.description || ''}
              >
                {row?.description || ''}
              </h6>
            </>
          );
        },
      },

      {
        id: 'statusHPS2',
        name: <span className=' text-uppercase fw-semibold fs-13'>Status</span>,
        selector: row => (row?.statusHPS2 ? 'Available' : 'Not Available'),
        sortable: true,
        width: '10%',
        cell: row => {
          return (
            <div
              className={`badge fs-12 ${
                row.invitationEvaluationStatus === 'Confirmed'
                  ? 'bg-success'
                  : 'bg-info'
              }`}
            >
              {row.invitationEvaluationStatus}
            </div>
          );
        },
      },
      {
        name: <span className=' text-uppercase fw-semibold fs-13'>Action</span>,

        sortable: false,
        width: '7%',
        className: 'text-end',
        cell: row => (
          <div className='d-flex gap-1 justify-content-end'>
            <Link
              to={`../apps-evaluation-invitation/upd/${row?.id || ''}`}
              className='btn btn-soft-info btn-sm btn-icon'
            >
              <i className='ri-eye-line align-bottom'></i>
            </Link>
          </div>
        ),
      },
    ],
    [handleDeleteModal]
  );

  const renderMobileView = () => (
    <div className='d-md-none'>
      {isLoading ? (
        <div className='table-loading'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
          <p className='mt-2'>Loading procurement data...</p>
        </div>
      ) : data.items?.length > 0 ? (
        data.items.map(item => <MobileCard key={item.id} item={item} />)
      ) : (
        <div className='table-empty'>
          <i
            className='ri-inbox-line'
            style={{ fontSize: '3rem', opacity: 0.5 }}
          ></i>
          <p>No procurement data found</p>
        </div>
      )}
    </div>
  );

  const renderDesktopView = () => (
    <div className='d-none d-md-block'>
      <div
        ref={tableContainerRef}
        className='table-responsive'
        style={{ maxHeight: '600px', overflowY: 'auto' }}
      >
        {isLoading ? (
          <div className='table-loading'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
            <p className='mt-2'>Loading procurement data...</p>
          </div>
        ) : (
          <DataTable
            key={`sort-${getCurrentSortColumnId()}-${sortDirection}`}
            columns={columns}
            data={data.items || []}
            pagination={false}
            onSort={handleSort}
            sortServer
            defaultSortFieldId={getCurrentSortColumnId()}
            defaultSortAsc={sortDirection === 'asc'}
            highlightOnHover
            responsive
            striped
            persistTableHead
            progressPending={isLoading}
            progressComponent={
              <div className='text-center py-4'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='sr-only'>Loading...</span>
                </div>
                <p className='mt-2 text-muted'>Loading data...</p>
              </div>
            }
            dense
            customStyles={{
              header: {
                style: {
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  fontWeight: 'bold',
                  minHeight: '40px',
                },
              },
              headCells: {
                style: {
                  backgroundColor: '#f0f0f0',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                },
              },
              cells: {
                style: {
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  wordBreak: 'break-word',
                  paddingTop: '4px',
                  paddingBottom: '4px',
                },
              },
              rows: {
                style: {
                  minHeight: '45px',
                },
              },
              table: {
                style: {
                  width: '100%',
                },
              },
              tableWrapper: {
                style: {
                  display: 'table',
                },
              },
            }}
            noDataComponent={
              <div className='table-empty'>
                <i
                  className='ri-inbox-line'
                  style={{ fontSize: '3rem', opacity: 0.5 }}
                ></i>
                <p>No procurement data found</p>
              </div>
            }
          />
        )}
      </div>
    </div>
  );

  const renderPagination = () => (
    <div className='d-flex justify-content-between align-items-center mt-3 px-3 py-2 border-top'>
      <div className='d-flex align-items-center'>
        <span className='text-muted me-2'>Rows per page:</span>
        <select
          className='form-select form-select-sm'
          style={{ width: 'auto' }}
          value={rowsPerPage}
          onChange={e => handlePerRowsChange(parseInt(e.target.value))}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>

      <div className='d-flex align-items-center'>
        <span className='text-muted me-3'>
          {totalRows > 0 ? (
            <>
              {(currentPage - 1) * rowsPerPage + 1}-
              {Math.min(currentPage * rowsPerPage, totalRows)} of {totalRows}
            </>
          ) : (
            '0 of 0'
          )}
        </span>

        <div className='btn-group' role='group'>
          <button
            type='button'
            className='btn btn-outline-secondary btn-sm'
            disabled={currentPage === 1 || isLoading}
            onClick={() => handlePageChange(1)}
          >
            <i className='ri-skip-back-line'></i>
          </button>
          <button
            type='button'
            className='btn btn-outline-secondary btn-sm'
            disabled={currentPage === 1 || isLoading}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <i className='ri-arrow-left-s-line'></i>
          </button>
          <span className='btn btn-outline-secondary btn-sm disabled'>
            {currentPage}
          </span>
          <button
            type='button'
            className='btn btn-outline-secondary btn-sm'
            disabled={
              currentPage >= Math.ceil(totalRows / rowsPerPage) || isLoading
            }
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <i className='ri-arrow-right-s-line'></i>
          </button>
          <button
            type='button'
            className='btn btn-outline-secondary btn-sm'
            disabled={
              currentPage >= Math.ceil(totalRows / rowsPerPage) || isLoading
            }
            onClick={() => handlePageChange(Math.ceil(totalRows / rowsPerPage))}
          >
            <i className='ri-skip-forward-line'></i>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className='pt-0'>
            <Row className='mb-3'>
              <Col xs={12}>
                <Row className='g-3'>
                  <Col xs={12} md={6} lg={4}>
                    <Label className='form-label'>Search</Label>
                    <Input
                      type='text'
                      placeholder='Search by title or Procurement No...'
                      value={searchText || ''}
                      onChange={handleSearch}
                      className='form-control'
                    />
                  </Col>

                  <Col xs={12} md={6} lg={3} className='d-flex align-items-end'>
                    <Button
                      color='secondary'
                      outline
                      onClick={clearFilters}
                      className='w-100'
                    >
                      <i className='ri-refresh-line me-1'></i>
                      <span className='d-none d-sm-inline'>Clear Filters</span>
                      <span className='d-sm-none'>Clear</span>
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row className='mb-2'>
              <Col xs={12}>
                <div className='d-flex justify-content-between align-items-center flex-wrap'>
                  <small className='text-muted'>
                    Showing {data.items?.length || 0} of {totalRows} entries
                    {searchText && ' (filtered)'}
                  </small>
                  {searchText && (
                    <div className='d-flex align-items-center'>
                      <span className='badge bg-primary me-2'>
                        <i className='ri-search-line me-1'></i>
                        Search Active
                      </span>
                    </div>
                  )}
                </div>
              </Col>
            </Row>

            {renderMobileView()}
            {renderDesktopView()}
            {renderPagination()}
          </div>
        </Col>
      </Row>
    </>
  );
};

export default React.memo(InvitationEvaluationTable);
